import { Button } from '@/global/components/ui/button'
import { PopoverTrigger } from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDownIcon, FilterIcon } from 'lucide-react'
import { ICONES_FACETA } from './Cascata'
import { useEscopoContext } from './context'
import { type FacetaId, palavraContagem } from './escopo'

export const triggerVariants = cva(
    'w-[27.5rem] shrink-0 justify-between text-sm font-normal transition-all duration-150',
    {
        variants: {
            variant: {
                outlined:
                    'bg-background hover:bg-accent/40 border border-solid border-border/80 hover:border-primary/50 rounded-sm shadow-xs data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/20',
                filled: 'relative bg-muted/80 hover:bg-foreground/[0.09] border-b border-solid border-b-foreground/40 hover:border-b-foreground/70 dark:border-b-foreground/40 border-t-0 border-x-0 rounded-sm rounded-b-none shadow-none data-[state=open]:bg-muted/80 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:origin-center after:scale-x-0 after:transition-transform after:duration-300 after:ease-out data-[state=open]:after:scale-x-100',
                standard:
                    'relative bg-transparent hover:bg-muted/20 border-b border-solid border-b-foreground/40 hover:border-b-foreground/70 dark:border-b-foreground/40 border-t-0 border-x-0 rounded-none shadow-none px-1 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:origin-center after:scale-x-0 after:transition-transform after:duration-300 after:ease-out data-[state=open]:after:scale-x-100',
            },
            size: {
                sm: 'h-10 gap-2.5 px-3 py-2',
                md: 'h-12 gap-3 px-3 pt-1 pb-1',
                lg: 'h-14 gap-3 px-3 pt-[7px] pb-2',
            },
        },
        defaultVariants: {
            variant: 'outlined',
            size: 'sm',
        },
    },
)

export type TriggerProps = VariantProps<typeof triggerVariants> & {
    className?: string
    label?: string
}

const TEXTO_PRINCIPAL =
    'min-w-0 flex-1 truncate text-left text-base font-normal leading-[23px] text-foreground'

/**
 * Pílula de uma faceta: colapsada mostra ícone + contagem. No hover cresce e
 * revela o tipo do filtro ao lado (`1 centro de custo`). Sem hover, o tipo de
 * cada faceta também aparece no rail do painel (`Cascata`).
 *
 * `prefixo` é o `+` do badge de empresas, decidido em `montarResumoVisual`
 * (`+2`, `+2 empresas`).
 */
function PilulaFaceta({
    id,
    total,
    prefixo = '',
}: {
    id: FacetaId
    total: number
    prefixo?: string
}) {
    const Icone = ICONES_FACETA[id]

    return (
        <span className="group/badge flex shrink-0 items-center whitespace-nowrap rounded-full border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary transition-[padding] duration-200 hover:px-2">
            <Icone className="size-3.5 shrink-0" />
            <span className="pl-1 tabular-nums">{`${prefixo}${total}`}</span>
            {/* truque de grid 0fr → 1fr: única forma de animar width auto */}
            <span className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-200 group-hover/badge:grid-cols-[1fr]">
                <span className="min-w-0 overflow-hidden">
                    <span className="pl-1">{palavraContagem(id, total)}</span>
                </span>
            </span>
        </span>
    )
}

/**
 * Botão do filtro, de largura fixa: nome da empresa em texto truncado + um
 * badge por faceta com seleção. Só o nome da empresa cede espaço.
 */
export function Trigger({
    className,
    variant = 'outlined',
    size = 'sm',
    label = 'Filtro',
}: TriggerProps) {
    const { resumoVisual } = useEscopoContext()
    const {
        empresaTexto,
        funcionarioTexto,
        empresasExtras,
        empresasPrefixo,
        badges,
    } = resumoVisual
    // em tela de um funcionário só, é o nome dele que o botão mostra (#1870)
    const textoPrincipal = funcionarioTexto ?? empresaTexto
    const isStacked = size === 'md' || size === 'lg'

    const temBadges = empresasExtras > 0 || badges.length > 0

    const listaBadges = temBadges ? (
        <div
            className={cn(
                'flex shrink-0 items-center gap-1',
                isStacked && 'mb-1',
            )}
        >
            {empresasExtras > 0 && (
                <PilulaFaceta
                    id="empresas"
                    total={empresasExtras}
                    prefixo={empresasPrefixo}
                />
            )}
            {badges.map(badge => (
                <PilulaFaceta
                    key={badge.id}
                    id={badge.id}
                    total={badge.total}
                />
            ))}
        </div>
    ) : null

    const conteudo = temBadges ? (
        <>
            {textoPrincipal && (
                <span className={TEXTO_PRINCIPAL}>{textoPrincipal}</span>
            )}
            {listaBadges}
        </>
    ) : (
        <span className={TEXTO_PRINCIPAL}>
            {textoPrincipal ?? 'Nenhum filtro'}
        </span>
    )

    return (
        <PopoverTrigger asChild>
            <Button
                type="button"
                variant="ghost"
                aria-label="Filtro do relatório"
                className={cn(
                    'group/trigger',
                    triggerVariants({ variant, size }),
                    className,
                )}
            >
                {isStacked ? (
                    <div className="flex min-w-0 flex-1 flex-col text-left">
                        <span className="mt-1 truncate text-xs font-normal leading-[17.25px] text-foreground">
                            {label}
                        </span>
                        <div className="flex min-w-0 items-center -mt-0.5 gap-1.5 leading-[23px]">
                            {conteudo}
                        </div>
                    </div>
                ) : (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <FilterIcon className="size-4 shrink-0 text-muted-foreground" />
                        {conteudo}
                    </div>
                )}
                <ChevronDownIcon className="size-5 shrink-0 text-foreground/55 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
            </Button>
        </PopoverTrigger>
    )
}
